import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import PageHeader from '../components/common/PageHeader';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';

const UserForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditing = !!id;

  const initialFormData = {
    name: '',
    email: '',
    phone: '',
    password: '',
    role: 'user',
    isVerified: true,
    status: 'active'
  };

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(isEditing);

  useEffect(() => {
    if (isEditing) {
      if (location.state?.user) {
        populateForm(location.state.user);
        setLoading(false);
      } else {
        fetchUserData();
      }
    }
  }, [isEditing, id]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getAllUsers();
      const user = res.data.data.find(u => u._id === id);
      if (user) {
        populateForm(user);
      } else {
        toast.error('User not found');
        navigate('/users');
      }
    } catch (error) {
      toast.error('Failed to load user');
      navigate('/users');
    } finally {
      setLoading(false);
    }
  };

  const populateForm = (user) => {
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      password: '', // Never populate password
      role: user.role || 'user',
      isVerified: user.isVerified !== false,
      status: user.isActive ? 'active' : 'inactive'
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData };
      if (isEditing && !payload.password) {
        delete payload.password;
      }
      
      if (isEditing) {
        await adminApi.updateUser(id, payload);
        toast.success('User updated successfully');
      } else {
        if (!payload.password) {
          return toast.error('Password is required for new users');
        }
        await adminApi.createUser(payload);
        toast.success('User created successfully');
      }
      setTimeout(() => navigate('/users'), 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save user');
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-transparent p-12 text-center text-gray-500 font-bold">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title={isEditing ? 'Edit User' : 'Create New User'}
        subtitle="Manage user details, roles, and status."
        backLink="/users"
      />

      <main className="max-w-full mx-auto">
        <div className="bg-white rounded-[14px] shadow-[0_8px_30px_rgb(0,0,0,0.02)] overflow-hidden border-t-[6px] border-red-600">
          <form onSubmit={handleSave} className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              
              {/* Account Details */}
              <div className="space-y-5">
                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">1</span>
                  Account Information
                </h3>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Full Name</label>
                  <input 
                    required type="text" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 outline-none transition-all" 
                    value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} 
                    placeholder="John Doe"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Email Address</label>
                  <input 
                    required type="email" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 outline-none transition-all" 
                    value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} 
                    placeholder="john@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">Phone Number</label>
                  <input 
                    required type="tel" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 outline-none transition-all" 
                    value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} 
                    placeholder="9876543210"
                  />
                </div>

                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">
                    Password {isEditing && <span className="text-gray-400 font-medium">(Leave blank to keep current)</span>}
                  </label>
                  <input 
                    type="password" className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-red-500 font-bold text-gray-900 outline-none transition-all" 
                    value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} 
                    placeholder={isEditing ? '••••••••' : 'Enter a strong password'}
                  />
                </div>
              </div>
              
              {/* Access & Status */}
              <div className="space-y-5 bg-gray-50 p-6 rounded-2xl border border-gray-100">
                <h3 className="text-xs font-extrabold text-gray-400 uppercase tracking-widest flex items-center mb-1">
                  <span className="w-6 h-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center mr-2">2</span>
                  Access & Settings
                </h3>
                
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-1.5">User Role</label>
                  <select 
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl bg-white font-bold text-gray-900 outline-none transition-all focus:ring-2 focus:ring-red-500"
                    value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})}
                  >
                    <option value="user">User (Business Owner)</option>
                    <option value="admin">Admin</option>
                    <option value="super-admin">Super Admin</option>
                  </select>
                </div>

                <div className="pt-4 flex flex-col space-y-4">
                  <label className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:border-red-200 transition-colors">
                    <div>
                      <span className="text-sm font-extrabold text-gray-900 block">Account Verified</span>
                      <span className="text-xs font-medium text-gray-500">Email/Phone is verified</span>
                    </div>
                    <div className="relative flex items-center">
                      <input 
                        type="checkbox" 
                        checked={formData.isVerified} 
                        onChange={e => setFormData({...formData, isVerified: e.target.checked})} 
                        className="peer sr-only" 
                      />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-green-500 transition-all after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white`}></div>
                    </div>
                  </label>
                  
                  <label className="flex items-center justify-between p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:border-red-200 transition-colors">
                    <div>
                      <span className="text-sm font-extrabold text-gray-900 block">Active Status</span>
                      <span className="text-xs font-medium text-gray-500">User can log in to the system</span>
                    </div>
                    <div className="relative flex items-center">
                      <input 
                        type="checkbox" 
                        checked={formData.status === 'active'} 
                        onChange={e => setFormData({...formData, status: e.target.checked ? 'active' : 'inactive'})} 
                        className="peer sr-only" 
                      />
                      <div className={`w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-red-600 transition-all after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-5 peer-checked:after:border-white`}></div>
                    </div>
                  </label>
                </div>
              </div>
              
            </div>
            
            <div className="pt-6 border-t border-gray-100 flex justify-end space-x-4">
              <button 
                type="button" 
                onClick={() => navigate('/users')} 
                className="px-8 py-3 rounded-full text-sm font-bold text-gray-700 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="px-8 py-3 rounded-full text-sm font-bold bg-red-600 text-white hover:bg-red-700 shadow-md transition-all transform hover:-translate-y-0.5 hover:shadow-lg flex items-center"
              >
                {isEditing ? 'Save Changes' : 'Create User'}
                <ArrowLeftIcon className="h-4 w-4 ml-2 transform rotate-180" />
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default UserForm;
